import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Chip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import axios from "../../../config/axiosConfig";
import MuiAlert from "@mui/material/Alert";
import CentroEquipoDetalleDialog from "../../requerimiento/components/CentroEquipoDetalleDialog";
import { useAuth } from "../../../context/AuthContext";

// Estado 1-2 (Emitido/Detalle) de una OT de Reemplazo (tipo_ot=4): espejo de
// index.php + asignacion.php del sistema viejo — acá solo se elige QUÉ equipo Activo
// del cliente sale (checkbox). El estado final (Inactivo/Mantenimiento/Baja/Robado) y
// la observación se capturan después, en el momento de asignarle el reemplazo (ver
// AsignarReemplazoOT.jsx) — a pedido del usuario, para no pedir esos datos antes de
// tiempo. El propio envío avanza la OT a "Asignado" (3).
// AVL/Scada no entra acá: ese Reemplazo ya lo cubre el módulo Requerimiento.
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };
const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
};

const TIPOS_TKR = ["Radio", "Bateria"];
const TIPOS_OTROS = ["Varios"];
const TIPO_LABEL = { Radio: "Radios", Bateria: "Baterías", Varios: "Equipos" };
const PAGE_SIZE = 15;

const endpointDeTipo = (tipo) => {
  if (tipo === "Radio") return "/api/equipos-radio";
  if (tipo === "Bateria") return "/api/equipos-bateria";
  return "/api/equipos-varios";
};

const SeleccionarReemplazoOT = ({ cod_ot, cod_cliente, cod_servicio_cliente, esTKR, onEnviado }) => {
  const { user } = useAuth();
  const tipos = esTKR ? TIPOS_TKR : TIPOS_OTROS;
  const [tipo, setTipo] = useState(tipos[0]);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [buscar, setBuscar] = useState("");
  const [buscarAplicado, setBuscarAplicado] = useState("");
  const [loadingEquipos, setLoadingEquipos] = useState(true);
  const [error, setError] = useState("");

  // { [key]: { tipoEquipo, identificador } }
  const [seleccion, setSeleccion] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [errores, setErrores] = useState([]);
  const [equipoDialog, setEquipoDialog] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cargarPagina = (nextPage = page, buscarTexto = buscarAplicado) => {
    setLoadingEquipos(true);
    setError("");
    axios
      .get(endpointDeTipo(tipo), {
        params: {
          estado: "Activo",
          cod_servicio: cod_servicio_cliente,
          // Solo equipo de la sucursal del usuario logueado — mismo criterio que
          // DevolucionEquiposOT/SeleccionarMantenimientoOT: no tiene sentido
          // reemplazar equipo que está físicamente en otra ciudad.
          ciudad: user?.sucursal || undefined,
          buscar: buscarTexto,
          page: nextPage,
          pageSize: PAGE_SIZE,
        },
      })
      .then((res) => {
        const data = res.data || {};
        setItems((data.items || []).map((r) => ({ key: `${tipo.toLowerCase()}-${r.ID}`, tipoEquipo: tipo, identificador: r.serie, modelo: r.modelo, ciudad: r.ciudad })));
        setTotal(Number(data.total || 0));
        setPage(Number(data.page || nextPage));
      })
      .catch((err) => {
        console.error("Error al cargar equipos activos del cliente", err);
        setError("No se pudo cargar el equipo activo del cliente.");
      })
      .finally(() => setLoadingEquipos(false));
  };

  useEffect(() => {
    setBuscar("");
    setBuscarAplicado("");
    cargarPagina(1, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, cod_servicio_cliente]);

  useEffect(() => {
    setTipo((esTKR ? TIPOS_TKR : TIPOS_OTROS)[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esTKR]);

  const handleBuscar = () => {
    setBuscarAplicado(buscar.trim());
    cargarPagina(1, buscar.trim());
  };

  const toggleEquipo = (row) => {
    setSeleccion((prev) => {
      if (prev[row.key]) {
        const { [row.key]: _quitado, ...resto } = prev;
        return resto;
      }
      return {
        ...prev,
        [row.key]: { tipoEquipo: row.tipoEquipo, identificador: row.identificador },
      };
    });
  };

  const seleccionados = Object.values(seleccion);

  const handleEnviar = async () => {
    if (!seleccionados.length) return;
    if (!window.confirm(`¿Marcar ${seleccionados.length} equipo(s) para Reemplazo? La OT pasará a "Asignado".`)) return;
    setEnviando(true);
    setErrores([]);
    try {
      const familiaDe = (t) => (t === "Radio" ? "RADIO" : t === "Bateria" ? "BATERIA" : "VARIOS");
      const payload = {
        cod_cliente,
        cod_servicio_cliente,
        seleccion: seleccionados.map((s) => ({
          familia: familiaDe(s.tipoEquipo),
          identificador: s.identificador,
        })),
      };
      const { data } = await axios.post(`/api/ot-reemplazo/seleccionar/${cod_ot}`, payload);
      if (onEnviado) onEnviado(data);
    } catch (err) {
      setErrores([err?.response?.data?.error || "No se pudo enviar la selección de reemplazo."]);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Equipos Activos del Cliente
      </Typography>

      {tipos.length > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <ToggleButtonGroup
            size="small"
            exclusive
            color="primary"
            value={tipo}
            onChange={(e, nuevo) => nuevo && setTipo(nuevo)}
            sx={{ height: 30 }}
          >
            {tipos.map((t) => (
              <ToggleButton key={t} value={t} sx={{ fontSize: 11.5, px: 1.5, py: 0.35, textTransform: "none" }}>
                {TIPO_LABEL[t]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mb: 1, rowGap: 1 }}>
        <TextField
          size="small"
          label="Buscar"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleBuscar(); }}
          sx={{ minWidth: 160, ...controlSx }}
        />
        <IconButton size="small" onClick={handleBuscar} title="Buscar">
          <SearchIcon fontSize="small" />
        </IconButton>
        <Chip size="small" label={`Total: ${total}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" color="warning" variant="outlined" label={`Seleccionados: ${seleccionados.length}`} sx={{ height: 20, fontSize: 11 }} />
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 0.5, maxHeight: 360, borderRadius: 0.5 }}>
        <Table size="small" stickyHeader sx={tableSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: 32 }}>Sel.</TableCell>
              <TableCell sx={headCellSx}>Identificador</TableCell>
              <TableCell sx={headCellSx}>Modelo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingEquipos ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            ) : !items.length ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Este cliente no tiene {TIPO_LABEL[tipo].toLowerCase()} activos.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              items.map((row) => {
                const checked = Boolean(seleccion[row.key]);
                return (
                  <TableRow key={row.key} hover selected={checked}>
                    <TableCell>
                      <Checkbox size="small" checked={checked} onChange={() => toggleEquipo(row)} sx={{ p: 0 }} />
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        onClick={() => setEquipoDialog({ tipo: row.tipoEquipo.toLowerCase(), identificador: row.identificador })}
                        sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                      >
                        {row.identificador}
                      </Box>
                    </TableCell>
                    <TableCell>{row.modelo}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Página {total ? page : 0} / {total ? totalPages : 0}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<NavigateBeforeIcon />} disabled={loadingEquipos || page <= 1}
          onClick={() => cargarPagina(page - 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Anterior
        </Button>
        <Button size="small" variant="outlined" endIcon={<NavigateNextIcon />} disabled={loadingEquipos || page >= totalPages}
          onClick={() => cargarPagina(page + 1)} sx={{ minHeight: 26, height: 26, fontSize: 11 }}>
          Siguiente
        </Button>
      </Stack>

      {!!errores.length && (
        <MuiAlert severity="error" variant="outlined" sx={{ mb: 1, fontSize: 11, py: 0 }}>
          {errores.map((msg, i) => (<div key={i}>{msg}</div>))}
        </MuiAlert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!seleccionados.length || enviando}
          onClick={handleEnviar}
          sx={{ minHeight: 28, height: 28, fontSize: 11.5, px: 2 }}
        >
          {enviando ? <CircularProgress size={16} color="inherit" /> : "Enviar a Reemplazo"}
        </Button>
      </Box>

      <CentroEquipoDetalleDialog
        open={!!equipoDialog}
        tipo={equipoDialog?.tipo}
        identificador={equipoDialog?.identificador}
        onClose={() => setEquipoDialog(null)}
      />
    </Box>
  );
};

export default SeleccionarReemplazoOT;
